import {
  Callout,
  DefaultButton,
  DirectionalHint,
  IconButton,
  PrimaryButton,
} from "@fluentui/react";
import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import { BooleanFilter } from "./BooleanFilter";
import { ColumnOptionsContext } from "./ColumnOptions";
import { DateFilter } from "./DateFilter";
import { NumberFilter } from "./NumberFilter";
import { TaxonomyFilter } from "./TaxonomyFilter";
import { TaxonomySelectFilter } from "./TaxonomySelectFilter";
import { TextFilter } from "./TextFilter";
import { Column } from "./types";
import { clearFilter } from "./utils";

type Props = {
  isOpen: boolean;
  close: () => void;
  target: React.RefObject<HTMLDivElement>;
  column: Column;
};

// Keep the filter callout open while interacting with nested Fluent pickers
// (DatePicker calendar, Dropdown menu, TagPicker suggestions) which render in
// their own layers. Intentionally narrow: matching broad containers such as
// `.ms-Layer`, `.ms-Callout-main` or `[role='dialog']` would also match the
// surrounding column-options modal, blocking dismissal on every click inside
// the panel (which is why outside-clicks stopped closing the filter).
const preventDismissOnEvent = (ev: any) => {
  const target = ev?.target as HTMLElement | undefined;
  if (target && typeof target.closest === "function") {
    if (
      target.closest(
        ".ms-Calendar, .ms-DatePicker, .ms-Dropdown-callout, .ms-Suggestions, [role='listbox'], [role='option']"
      )
    ) {
      return true;
    }
  }
  return false;
};

export const FilterDropdown = ({ isOpen, close, target, column }: Props) => {
  const { setFilter, strings, id } = useContext(ColumnOptionsContext);

  const disableClearButton = useMemo(() => {
    if (!column.filter) return {} as any;
    return {
      boolean:
        column.filter?.type === "boolean" && column.filter?.value === null,
      text: column.filter?.type === "text" && column.filter?.value === "",
      date: column.filter?.type === "date" && column.filter?.date1 === null,
      number:
        column.filter?.type === "number" && column.filter?.number1 === null,
      taxonomy:
        column.filter?.type === "taxonomy" && !column.filter?.value.length,
    };
  }, [column.filter]);

  if (!column.filter) return null;
  if (!target.current) return null;

  const isDisabled = disableClearButton[column.filter.type];

  return (
    <Callout
      target={target.current}
      onDismiss={close}
      directionalHint={DirectionalHint.bottomLeftEdge}
      isBeakVisible={false}
      gapSpace={2}
      preventDismissOnEvent={preventDismissOnEvent}
      setInitialFocus
      styles={{ calloutMain: { minWidth: 260 } }}
    >
      <Surface data-filter-dropdown={column.key}>
        <FilterHeader>
          <FilterTitle title={column.label}>{column.label}</FilterTitle>
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            ariaLabel={strings.closeButtonLabel}
            title={strings.closeButtonLabel}
            onClick={close}
            id={id ? `${id}-close-filter-${column.key}` : undefined}
            styles={{
              root: { minWidth: 24, width: 24, height: 24, padding: 0 },
            }}
          />
        </FilterHeader>
        <FilterContent>
          {column.filter.type === "taxonomy" && !column.filter.isSelect && (
            <TaxonomyFilter column={column} />
          )}
          {column.filter.type === "taxonomy" && column.filter.isSelect && (
            <TaxonomySelectFilter column={column} />
          )}
          {column.filter.type === "date" && <DateFilter column={column} />}
          {column.filter.type === "number" && <NumberFilter column={column} />}
          {column.filter.type === "boolean" && (
            <BooleanFilter column={column} />
          )}
          {column.filter.type === "text" && <TextFilter column={column} />}
        </FilterContent>
        <FilterActions>
          <DefaultButton
            id={id ? `${id}-clear-${column.key}` : undefined}
            disabled={isDisabled}
            iconProps={{ iconName: "Cancel" }}
            onClick={() => {
              if (!column.filter) return;
              setFilter(column.key, clearFilter(column.filter));
            }}
            text={strings.clearFilterButtonLabel}
          />
          <PrimaryButton
            id={id ? `${id}-apply-filter-${column.key}` : undefined}
            iconProps={{ iconName: "CheckMark" }}
            onClick={close}
            text={strings.applyButtonLabel}
          />
        </FilterActions>
      </Surface>
    </Callout>
  );
};

const Surface = styled.div`
  min-width: 260px;
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px 6px 12px;
  border-bottom: 1px solid ${(props) => props.theme.palette.neutralLight};
`;

const FilterTitle = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  font-size: 13px;
`;

const FilterContent = styled.div`
  padding: 12px;
  overflow-y: auto;
  max-height: 320px;
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid ${(props) => props.theme.palette.neutralLight};
`;
